import React from "react";

const OPTION_LABELS = ["A", "B", "C", "D"];

const TYPE_BADGE = {
  multiple_choice: { label: "MCQ", bg: "#F3E8FF", color: "#7C3AED" },
  boolean: { label: "Yes / No", bg: "#FEF9C3", color: "#B45309" },
  subjective: { label: "Subjective", bg: "#EFF6FF", color: "#2563EB" },
};

function InterviewKit({ questions, responses, setResponses }) {
  const getAnswer = (qi) => responses[qi]?.answer;
  const getWeightage = (qi) => responses[qi]?.response_weightage ?? 0;

  const totalMax = questions.reduce((sum, q) => sum + q.weightage, 0);
  const totalEarned = questions.reduce((sum, q) => sum + getWeightage(q.index), 0);
  const totalPct = totalMax > 0 ? Math.round((totalEarned / totalMax) * 100) : 0;
  const totalBarColor = totalEarned === totalMax ? "#16A34A" : totalEarned > 0 ? "#2563EB" : "#CBD5E1";

  // Select MCQ answer — auto-fill full score if correct answer picked
  const handleMCQSelect = (qi, optIdx, correctOption, maxWeightage) => {
    const isCorrect = optIdx === correctOption;
    setResponses((prev) => ({
      ...prev,
      [qi]: {
        ...prev[qi],
        answer: optIdx,
        response_weightage: isCorrect ? maxWeightage : 0,
      },
    }));
  };

  // Select boolean answer — auto-fill full score if correct answer picked
  const handleBooleanSelect = (qi, val, correctAnswer, maxWeightage) => {
    const isCorrect = val === correctAnswer;
    setResponses((prev) => ({
      ...prev,
      [qi]: {
        ...prev[qi],
        answer: val,
        response_weightage: isCorrect ? maxWeightage : 0,
      },
    }));
  };

  const handleSubjectiveChange = (qi, value) =>
    setResponses((prev) => ({ ...prev, [qi]: { ...prev[qi], answer: value } }));

 

  const handleScoreChange = (qi, value) =>
    setResponses((prev) => ({
      ...prev,
      [qi]: { ...prev[qi], response_weightage: Number(value) },
    }));

  if (!questions || questions.length === 0) return null;

  return (
    <div style={{ marginTop: 24, marginBottom: 8 }}>
      {/* Section heading */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <h3
          style={{
            fontFamily: "gilRoy-semibold",
            fontSize: 14,
            color: "#1E293B",
            margin: 0,
          }}
        >
          Interview Kit Questions
        </h3>
        <div style={{ fontSize: 12, color: "#94A3B8" }}>
          ({questions.length} questions)
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {questions.map((q) => {
          const badge = TYPE_BADGE[q.question_type] || {
            label: q.question_type,
            bg: "#F1F5F9",
            color: "#64748B",
          };
          const selectedAnswer = getAnswer(q.index);
          const responseWeightage = getWeightage(q.index);
          const scoreColor = responseWeightage === q.weightage ? "#16A34A" : responseWeightage > 0 ? "#2563EB" : "#94A3B8";
          const scoreBg    = responseWeightage === q.weightage ? "#F0FDF4" : responseWeightage > 0 ? "#EFF6FF" : "#F8FAFC";
          const scoreBorder = responseWeightage === q.weightage ? "#BBF7D0" : responseWeightage > 0 ? "#BFDBFE" : "#E2E8F0";

          return (
            <div
              key={q.index}
              style={{
                border: "1px solid #CBD5E1",
                borderRadius: 8,
                backgroundColor: "#fff",
              }}
            >
              {/* ── Question header ── */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "14px 16px 10px 16px",
                }}
              >
                <div style={{ display: "flex", gap: 8, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#2563EB",
                      flexShrink: 0,
                    }}
                  >
                    Q{q.index + 1}.
                  </div>
                  <div
                    style={{ fontSize: 14, color: "#1E293B", lineHeight: 1.5 }}
                  >
                    {q.question}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 4,
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      backgroundColor: badge.bg,
                      color: badge.color,
                      padding: "2px 8px",
                      borderRadius: 4,
                    }}
                  >
                    {badge.label}
                  </div>
                  {/* Score badge */}
                  <div style={{
                    display: "inline-block",
                    fontSize: 12,
                    fontWeight: 700,
                    color: scoreColor,
                    border: `1.5px solid ${scoreBorder}`,
                    backgroundColor: scoreBg,
                    padding: "3px 10px",
                    borderRadius: 20,
                  }}>
                    {responseWeightage} / {q.weightage} pts
                  </div>
                </div>
              </div>

              <div style={{ padding: "0 16px 14px 16px" }}>
                {/* ── Multiple Choice ── */}
                {q.question_type === "multiple_choice" && (
                  <>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {q.options.map((option, optIdx) => {
                        const isSelected = selectedAnswer === optIdx;
                        const isCorrect =
                          q.probable_answer.correct_option === optIdx;
                        return (
                          <div
                            key={optIdx}
                            onClick={() =>
                              handleMCQSelect(
                                q.index,
                                optIdx,
                                q.probable_answer.correct_option,
                                q.weightage
                              )
                            }
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              padding: "9px 12px",
                              borderRadius: 6,
                              border: isSelected
                                ? "1px solid #2563EB"
                                : "1px solid #E2E8F0",
                              backgroundColor: isSelected
                                ? "#EFF6FF"
                                : "#FAFAFA",
                              cursor: "pointer",
                            }}
                          >
                            {/* Letter circle */}
                            <div
                              style={{
                                height: 22,
                                width: 22,
                                minWidth: 22,
                                borderRadius: "50%",
                                border: isSelected
                                  ? "1px solid #2563EB"
                                  : "1px solid #CBD5E1",
                                backgroundColor: isSelected
                                  ? "#2563EB"
                                  : "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 11,
                                fontWeight: 700,
                                color: isSelected ? "#fff" : "#64748B",
                              }}
                            >
                              {OPTION_LABELS[optIdx]}
                            </div>
                            <div
                              style={{
                                fontSize: 13,
                                color: isSelected ? "#2563EB" : "#334155",
                                flex: 1,
                              }}
                            >
                              {option}
                            </div>
                            {isCorrect && (
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "#16A34A",
                                  backgroundColor: "#F0FDF4",
                                  border: "1px solid #BBF7D0",
                                  padding: "2px 7px",
                                  borderRadius: 4,
                                  flexShrink: 0,
                                }}
                              >
                                ✓ Expected
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* ── Boolean ── */}
                {q.question_type === "boolean" && (
                  <>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {[true, false].map((val) => {
                        const isSelected = selectedAnswer === val;
                        const isCorrect = q.probable_answer.answer === val;
                        return (
                          <div
                            key={String(val)}
                            onClick={() =>
                              handleBooleanSelect(
                                q.index,
                                val,
                                q.probable_answer.answer,
                                q.weightage
                              )
                            }
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              padding: "9px 12px",
                              borderRadius: 6,
                              border: isSelected ? "1px solid #2563EB" : "1px solid #E2E8F0",
                              backgroundColor: isSelected ? "#EFF6FF" : "#FAFAFA",
                              cursor: "pointer",
                            }}
                          >
                            {/* T / F circle */}
                            <div
                              style={{
                                height: 22,
                                width: 22,
                                minWidth: 22,
                                borderRadius: "50%",
                                border: isSelected ? "1px solid #2563EB" : "1px solid #CBD5E1",
                                backgroundColor: isSelected ? "#2563EB" : "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 11,
                                fontWeight: 700,
                                color: isSelected ? "#fff" : "#64748B",
                              }}
                            >
                              {val ? "T" : "F"}
                            </div>
                            <div style={{ fontSize: 13, color: isSelected ? "#2563EB" : "#334155", flex: 1 }}>
                              {val ? "True" : "False"}
                            </div>
                            {isCorrect && (
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "#16A34A",
                                  backgroundColor: "#F0FDF4",
                                  border: "1px solid #BBF7D0",
                                  padding: "2px 7px",
                                  borderRadius: 4,
                                  flexShrink: 0,
                                }}
                              >
                                ✓ Expected
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {q.probable_answer.explanation && (
                      <div
                        style={{
                          marginTop: 8,
                          padding: "8px 12px",
                          backgroundColor: "#F0FDF4",
                          border: "1px solid #BBF7D0",
                          borderRadius: 6,
                          fontSize: 12,
                          color: "#166534",
                          lineHeight: 1.5,
                        }}
                      >
                        {q.probable_answer.explanation}
                      </div>
                    )}
                  </>
                )}

                {/* ── Subjective ── */}
                {q.question_type === "subjective" && (
                  <>
                    <textarea
                      rows={3}
                      placeholder="Enter candidate's response here..."
                      value={selectedAnswer || ""}
                      onChange={(e) =>
                        handleSubjectiveChange(q.index, e.target.value)
                      }
                      style={{
                        width: "100%",
                        border: "1px solid #CBD5E1",
                        borderRadius: 6,
                        padding: "10px 12px",
                        fontSize: 13,
                        color: "#1E293B",
                        resize: "none",
                        outline: "none",
                        boxSizing: "border-box",
                        fontFamily: "inherit",
                        backgroundColor: "#fff",
                      }}
                    />
                    {q.probable_answer.expected_points?.length > 0 && (
                      <div
                        style={{
                          marginTop: 10,
                          padding: "10px 12px",
                          backgroundColor: "#F0FDF4",
                          border: "1px solid #BBF7D0",
                          borderRadius: 6,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#16A34A",
                            marginBottom: 6,
                          }}
                        >
                          Expected Points to Cover:
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                          }}
                        >
                          {q.probable_answer.expected_points.map(
                            (point, pIdx) => (
                              <div
                                key={pIdx}
                                style={{
                                  display: "flex",
                                  gap: 6,
                                  fontSize: 12,
                                  color: "#166534",
                                }}
                              >
                                <div style={{ flexShrink: 0 }}>•</div>
                                <div>{point}</div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}

              </div>

              {/* ── Response Score strip ── */}
              {(() => {
                const isAutoType = q.question_type === "multiple_choice" || q.question_type === "boolean";
                // Auto-scored questions: slider always locked — full score if correct, 0 if wrong/unset
                const isLocked = isAutoType;
                return (
                  <div style={{
                    borderTop: `1px solid ${scoreBorder}`,
                    backgroundColor: scoreBg,
                    padding: "10px 16px 12px",
                    borderBottomLeftRadius: 8,
                    borderBottomRightRadius: 8,
                  }}>
                   
                    <input
                      type="range"
                      min={0}
                      max={q.weightage}
                      step={1}
                      value={responseWeightage}
                      onChange={(e) => handleScoreChange(q.index, e.target.value)}
                      disabled={isLocked}
                      style={{
                        display: "block",
                        width: "100%",
                        accentColor: scoreColor,
                        cursor: isLocked ? "not-allowed" : "pointer",
                        opacity: isLocked ? 0.4 : 1,
                      }}
                    />
                    {isAutoType && selectedAnswer !== undefined && (
                      <div style={{ marginTop: 4, fontSize: 11, color: "#64748B", fontWeight: 500 }}>
                        Score is auto-assigned based on selected answer
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>

      {/* ── Total Score Bar ── */}
      

    </div>
  );
}

export default InterviewKit;
